import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Field, Input } from "../../components/ui";
import { cognitoConfig, signUp } from "../../lib/cognito";
import { messageFor } from "../../lib/cognitoErrors";
import { checkPassword } from "../../lib/passwordPolicy";
import { safeNext, loginPath, DEFAULT_NEXT } from "../../lib/nextParam";
import { AuthAlert, AuthCard, AuthLink, PasswordChecklist } from "./parts";

/**
 * Create an account. Cognito leaves the user unconfirmed and emails a code, so
 * this hands off to /verify — carrying the password in router state, which is
 * what lets confirming sign the coach straight in.
 */
export function Signup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const next = safeNext(params.get("next"));
  const strong = checkPassword(password).valid;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!strong) return;
    setPending(true);
    setError(null);
    try {
      await signUp(await cognitoConfig(), email, password);
      const verify = new URLSearchParams({ email: email.trim() });
      if (next !== DEFAULT_NEXT) verify.set("next", next);
      navigate(`/verify?${verify.toString()}`, { state: { password } });
    } catch (err) {
      setError(messageFor(err).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthCard
      title="Create your account"
      subtitle="One account covers every team you coach, season after season."
      footer={
        <>
          Already have one? <AuthLink to={loginPath(next)}>Sign in</AuthLink>
        </>
      }
    >
      {error ? <AuthAlert>{error}</AuthAlert> : null}

      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <Field label="Email" hint="We'll send a verification code here.">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
        </Field>

        <Field label="Password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <PasswordChecklist password={password} />

        <Button type="submit" size="lg" disabled={pending || !strong} className="w-full">
          {pending ? "Creating your account…" : "Create account"}
        </Button>
      </form>
    </AuthCard>
  );
}
